import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { X, AlertTriangle, Trash2, Link2, Loader2, MapPin, Users, Archive } from "lucide-react";
import { conRuolo } from "@/lib/ruoli";

const ENTITY_LABEL = {
  proprietario: "Cliente Venditore",
  acquirente: "Cliente Acquirente",
  immobile: "Immobile",
};

const RESOLVABLE = {
  documenti: "Elimina documenti",
  visite: "Elimina visite",
  proposte: "Elimina proposte",
  appuntamenti: "Elimina appuntamenti",
  tasks: "Elimina task",
};

const REASSIGNABLE = new Set(["visite", "proposte", "appuntamenti", "tasks"]);

// entityType: "proprietario" | "acquirente" | "immobile"
// pathBase:   "/proprietari" | "/acquirenti" | "/immobili"
export default function DeleteEntityDialog({ entityType, id, pathBase, name, onClose, onDeleted }) {
  const isImmobile = entityType === "immobile";
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);      // { label, indirizzo, can_delete, blockers }
  const [step, setStep] = useState(1);         // 1 = first confirm, 2 = definitive confirm
  const [deleting, setDeleting] = useState(false);
  const [archiveStep, setArchiveStep] = useState(0);
  const [archiving, setArchiving] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const priv = !!user && (user.role === "direzione" || user.role === "segreteria" || user.is_segreteria);
  const vo = info?.blockers?.find((b) => b.tipo === "venditore_orfano");
  const openVenditore = () => { onClose(); navigate(`/proprietari?open=${vo.proprietario_id}`); };
  const doArchive = async () => {
    setArchiving(true);
    try {
      await api.post(`/immobili/${id}/elimina-archivia-venditore`);
      toast.success("Immobile eliminato e venditore archiviato.");
      onDeleted?.(); onClose();
    } catch (e) { toast.error(apiError(e)); setArchiveStep(0); } finally { setArchiving(false); }
  };
  const [busyTipo, setBusyTipo] = useState("");
  const [agents, setAgents] = useState([]);
  const [reassignTo, setReassignTo] = useState({});
  useEffect(() => {
    if (priv && isImmobile) {
      api.get("/users").then((r) => setAgents((r.data || []).filter((u) => u.active !== false && (u.role === "agente" || u.role === "direttore_commerciale" || u.role === "direzione" || (u.roles || []).includes("direzione")) && !u.archived_shared))).catch(() => {});
    }
  }, [priv, isImmobile]);
  const resolveBlocker = async (b) => {
    const msg = b.tipo === "documenti"
      ? "Eliminare tutti i documenti dell'immobile? L'operazione non è reversibile."
      : `Eliminare ${(b.label || b.tipo).toLowerCase()} collegati all'immobile? L'operazione non è reversibile.`;
    if (!window.confirm(msg)) return;
    setBusyTipo(b.tipo);
    try {
      const url = b.tipo === "documenti" ? `/immobili/${id}/risolvi-documenti` : `/immobili/${id}/risolvi/${b.tipo}`;
      await api.post(url);
      toast.success("Collegamento risolto.");
      loadLinks();
    } catch (e) { toast.error(apiError(e)); } finally { setBusyTipo(""); }
  };
  const reassignBlocker = async (b) => {
    const aid = reassignTo[b.tipo];
    if (!aid) { toast.error("Seleziona un agente."); return; }
    setBusyTipo(b.tipo);
    try {
      await api.post(`/immobili/${id}/riassegna/${b.tipo}`, { owner_agent_id: aid });
      toast.success("Collegamenti riassegnati all'agente.");
      loadLinks();
    } catch (e) { toast.error(apiError(e)); } finally { setBusyTipo(""); }
  };
  const doArchiveImmobile = async () => {
    setArchiving(true);
    try {
      await api.post(`/immobili/${id}/archivia`, { motivo: "Conservazione obbligatoria (antiriciclaggio, 10 anni)" });
      toast.success("Immobile archiviato (consultabile con 'Mostra archiviati').");
      onDeleted?.(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setArchiving(false); }
  };

  const label = info?.label || name || ENTITY_LABEL[entityType];
  const indirizzo = info?.indirizzo || "";

  const loadLinks = () => {
    setLoading(true);
    api.get(`${pathBase}/${id}/links`)
      .then((r) => setInfo(r.data))
      .catch((e) => { toast.error(apiError(e)); onClose(); })
      .finally(() => setLoading(false));
  };
  useEffect(loadLinks, []); // eslint-disable-line

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`${pathBase}/${id}`);
      toast.success(`${ENTITY_LABEL[entityType]} eliminato`);
      onDeleted?.();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d && typeof d === "object") {
        setInfo({ label: d.label || label, can_delete: false, blockers: d.blockers || [] });
        setStep(1);
        toast.error("Eliminazione bloccata: risolvi prima i collegamenti.");
      } else {
        toast.error(apiError(e));
      }
    } finally { setDeleting(false); }
  };

  // Per gli immobili non c'è mai blocco: tutti gli utenti autenticati possono eliminare.
  const blocked = info && !info.can_delete;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="delete-entity-dialog">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[calc(100vh-2rem)] flex flex-col card-immo overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-[var(--border)] shrink-0">
          <h3 className="font-display text-lg font-bold flex items-center gap-2 text-red-600">
            <Trash2 size={18} /> Elimina {ENTITY_LABEL[entityType]}
          </h3>
          <button onClick={onClose} data-testid="delete-dialog-close" className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18} /></button>
        </div>

        <div className="space-y-4 px-6 py-5 flex-1 overflow-y-auto overscroll-contain">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[var(--muted)] py-6 justify-center">
              <Loader2 className="animate-spin" size={16} /> Controllo dell'immobile…
            </div>
          ) : blocked ? (
            <div data-testid="delete-blocked">
              {info.concluso && priv && (
                <div data-testid="retention-archive" className="mb-3 rounded-xl border border-red-300 bg-red-50 p-3 space-y-2">
                  <div className="text-sm text-red-700">Questo immobile ha un rogito, un preliminare o una proposta accettata. Per <b>obbligo di conservazione antiriciclaggio (10 anni)</b> non può essere eliminato: puoi archiviarlo (resta consultabile con "Mostra archiviati" ed è ripristinabile).</div>
                  <button data-testid="archive-immobile-btn" onClick={doArchiveImmobile} disabled={archiving}
                          className="w-full py-2.5 rounded-xl bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
                    {archiving && <Loader2 className="animate-spin" size={14}/>}<Archive size={15}/> Archivia immobile
                  </button>
                </div>
              )}
              <div className="flex items-start gap-3 p-3 rounded-xl border border-[#FFB020]/40 bg-[#FFB020]/[0.08]">
                <AlertTriangle className="text-[#FFB020] shrink-0 mt-0.5" size={18} />
                <div className="text-sm text-slate-700">
                  Non puoi eliminare <b>{label}</b> perché ha record collegati. Trasferisci o risolvi prima i collegamenti indicati qui sotto.
                </div>
              </div>
              <div className="mt-3 space-y-2">
                {info.blockers.map((b) => (
                  <div key={b.tipo} data-testid={`blocker-${b.tipo}`} className="rounded-xl border border-[var(--border)] p-3">
                    <div className="flex items-center gap-2">
                      <Link2 size={14} className="text-[#FC82ED]" />
                      <span className="text-sm font-semibold">{b.label}</span>
                      <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{b.count}</span>
                    </div>
                    {b.items?.length > 0 && (
                      <div className="mt-1.5 text-xs text-slate-500 truncate">{b.items.join(" · ")}{b.count > b.items.length ? " …" : ""}</div>
                    )}
                    <div className="mt-1.5 text-[12px] text-[var(--muted)]">{b.azione}</div>
                    {priv && REASSIGNABLE.has(b.tipo) && (
                      <div className="mt-2 space-y-1.5" data-testid={`reassign-box-${b.tipo}`}>
                        <div className="flex gap-2">
                          <select data-testid={`reassign-select-${b.tipo}`} value={reassignTo[b.tipo] || ""}
                                  onChange={(e) => setReassignTo((m) => ({ ...m, [b.tipo]: e.target.value }))}
                                  className="flex-1 min-w-0 px-2.5 py-2 rounded-lg border border-[var(--border)] text-sm bg-white">
                            <option value="">Scegli agente…</option>
                            {agents.map((a) => <option key={a.id} value={a.id}>{conRuolo(a)}</option>)}
                          </select>
                          <button data-testid={`reassign-${b.tipo}`} onClick={() => reassignBlocker(b)} disabled={busyTipo === b.tipo || !reassignTo[b.tipo]}
                                  className="px-3 py-2 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5">
                            {busyTipo === b.tipo ? <Loader2 className="animate-spin" size={14}/> : <Users size={14}/>} Riassegna
                          </button>
                        </div>
                        <button data-testid={`resolve-${b.tipo}`} onClick={() => resolveBlocker(b)} disabled={!!busyTipo}
                                className="w-full py-1.5 rounded-lg border border-slate-200 text-slate-500 text-xs font-medium hover:border-red-300 hover:text-red-600 disabled:opacity-60 flex items-center justify-center gap-1.5">
                          <Trash2 size={13}/> {RESOLVABLE[b.tipo]}
                        </button>
                      </div>
                    )}
                    {priv && b.tipo === "documenti" && (
                      <button data-testid="resolve-documenti" onClick={() => resolveBlocker(b)} disabled={!!busyTipo}
                              className="mt-2 w-full py-2 rounded-lg border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50 disabled:opacity-60 flex items-center justify-center gap-2">
                        {busyTipo === "documenti" ? <Loader2 className="animate-spin" size={14}/> : <Trash2 size={14}/>} {RESOLVABLE.documenti}
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {vo && (
                <div className="mt-3 space-y-2" data-testid="venditore-orfano-actions">
                  <button data-testid="open-venditore-btn" onClick={openVenditore}
                          className="w-full py-2.5 rounded-xl bg-white border border-[var(--border)] text-sm font-semibold hover:border-[#FC82ED]/50 flex items-center justify-center gap-2"><Users size={15}/> Apri scheda venditore</button>
                  {vo.concluso ? (
                    <div data-testid="retention-block" className="rounded-xl border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2.5">
                      <b>{vo.proprietario_nome}</b> ha operazioni concluse (preliminari, rogiti o proposte accettate). Per obbligo di conservazione dei dati l'immobile non può essere eliminato.
                    </div>
                  ) : priv && (archiveStep === 0 ? (
                    <button data-testid="archive-venditore-btn" onClick={() => setArchiveStep(1)}
                            className="w-full py-2.5 rounded-xl border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50 flex items-center justify-center gap-2"><Archive size={15}/> Elimina immobile e archivia venditore</button>
                  ) : (
                    <div data-testid="archive-confirm" className="rounded-xl border border-red-300 bg-red-50 p-3 space-y-2">
                      <div className="text-sm text-red-700"><b>Conferma.</b> L'immobile verrà eliminato e <b>{vo.proprietario_nome}</b> spostato in Archiviati (ripristinabile).</div>
                      <div className="flex gap-2">
                        <button onClick={() => setArchiveStep(0)} className="flex-1 py-2 rounded-lg bg-white border border-[var(--border)] text-sm font-medium">Annulla</button>
                        <button data-testid="archive-confirm-btn" onClick={doArchive} disabled={archiving}
                                className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2">{archiving && <Loader2 className="animate-spin" size={14}/>} Conferma</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : step === 1 ? (
            <div data-testid="delete-confirm-1" className="text-sm text-slate-700">
              <div className="rounded-xl border border-[var(--border)] p-3 mb-3">
                <div className="font-semibold text-[#111111]">{label}</div>
                {isImmobile && indirizzo && (
                  <div className="text-xs text-[var(--muted)] mt-0.5 flex items-center gap-1.5"><MapPin size={12} className="text-[#FC82ED]" /> {indirizzo}</div>
                )}
              </div>
              {isImmobile ? (
                <>
                  Stai per eliminare definitivamente questo immobile. Verranno rimossi o annullati proposte, preliminari, visite, appuntamenti, checklist, documenti e attività operative collegate; il cliente venditore verrà scollegato (l'anagrafica resta).
                  <div className="mt-2 text-[var(--muted)]">Liste, contatori e dashboard verranno aggiornati.</div>
                </>
              ) : (
                <>Stai per eliminare <b>{label}</b>. L'operazione lo rimuoverà dagli elenchi attivi e aggiornerà i conteggi.
                  <div className="mt-2 text-[var(--muted)]">Nessun record collegato: puoi procedere.</div></>
              )}
            </div>
          ) : (
            <div data-testid="delete-confirm-2" className="text-sm text-red-700">
              <div className="flex items-start gap-3 p-3 rounded-xl border border-red-300 bg-red-50">
                <AlertTriangle className="text-red-500 shrink-0 mt-0.5" size={18} />
                <div>
                  <b>Conferma definitiva.</b> Questa azione è irreversibile: <b>{label}</b>{isImmobile && indirizzo ? ` — ${indirizzo}` : ""} verrà eliminato in modo permanente.
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2 px-6 py-4 border-t border-[var(--border)] shrink-0 bg-[var(--card)]">
          <button onClick={onClose} data-testid="delete-cancel-btn"
                  className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium hover:bg-slate-100">
            {blocked ? "Chiudi" : "Annulla"}
          </button>
          {!loading && !blocked && step === 1 && (
            <button onClick={() => setStep(2)} data-testid="delete-next-btn"
                    className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold text-sm">
              Elimina
            </button>
          )}
          {!loading && !blocked && step === 2 && (
            <button onClick={doDelete} disabled={deleting} data-testid="delete-confirm-btn"
                    className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2">
              {deleting && <Loader2 className="animate-spin" size={15} />} Conferma eliminazione definitiva
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
